import type { ReportingOperations } from "@aix/aixinternal-shared/reportingOperations"
import { deriveOperationalProduct } from "@aix/aixinternal-shared/reportingView"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@aix/ui"

const number = (value: number | null) =>
  value === null ? "Unknown" : value.toLocaleString("en-US", { maximumFractionDigits: 0 })
const rate = (value: number | null) => (value === null ? "Unknown" : `${(value * 100).toFixed(1)}%`)

export function OperationalReport({
  operations,
  productKey,
  productName,
}: {
  operations: ReportingOperations
  productKey: string
  productName: string
}) {
  const product = operations.products.find((candidate) => candidate.productKey === productKey)
  if (!product) return null
  const view = deriveOperationalProduct(product)
  const incidentRows = [
    ["Unified · current", view.incidents.currentUnified],
    ["Unified · prior", view.incidents.priorUnified],
    ["Legacy · current", view.incidents.currentLegacy],
    ["Legacy · prior", view.incidents.priorLegacy],
  ] as const
  return (
    <Card>
      <CardHeader>
        <CardTitle>{productName} · operational evidence</CardTitle>
        <CardDescription>
          {operations.current.startInclusive} → {operations.current.endExclusive} (exclusive). Pacific calendar week;
          latest-state evidence as of {operations.collectedAt}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Checks: {view.checks.status} · Incidents: {view.incidents.status} · Deployments: {view.deployments.status}
          </summary>
          <div className="mt-4 space-y-5">
            <section>
              <h3 className="mb-2 font-medium">Recorded-check summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Scope</th>
                      <th>Monitors</th>
                      <th>Passed / recorded</th>
                      <th>Pass rate</th>
                      <th>Prior rate</th>
                      <th>Unobserved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.checkGroups.map((group) => (
                      <tr key={group.key} className="border-t border-border">
                        <td className="py-2">
                          {group.kind} · {group.environment}
                        </td>
                        <td>{group.monitorCount}</td>
                        <td>
                          {group.current.passed} / {group.current.recorded}
                        </td>
                        <td>{rate(group.currentPassRate)}</td>
                        <td>{rate(group.priorPassRate)}</td>
                        <td>{number(group.current.unobserved)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!view.checkGroups.length ? (
                <p className="text-sm text-muted-foreground">No mapped check history.</p>
              ) : null}
              <p className="mt-2 text-xs text-muted-foreground">
                Recorded-check pass rate is not wall-clock uptime. Configured cadence is not a historical SLO.{" "}
                {view.checks.reasons.join(", ")}
              </p>
            </section>
            <section>
              <h3 className="mb-2 font-medium">Monitor inventory</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Monitor</th>
                      <th>Scope / cadence</th>
                      <th>Latest state</th>
                      <th>Last recorded</th>
                      <th>Mean latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.checks.rows.map((row) => (
                      <tr key={row.bindingKey} className="border-t border-border">
                        <td className="py-2">{row.name}</td>
                        <td>
                          {row.kind} / {row.environment} ·{" "}
                          {row.intervalSeconds === null ? "cadence unknown" : `${row.intervalSeconds}s`} ·{" "}
                          {row.enabledAtCollection ? "enabled" : "disabled"}
                        </td>
                        <td>
                          {row.lastSuccess === null ? "Unknown" : row.lastSuccess ? "Passed" : "Failed"}
                          {row.staleAtCollection === true ? " · STALE" : ""}
                        </td>
                        <td>{row.lastRecordedAt ?? "Never"}</td>
                        <td>
                          {row.current.latencySamples
                            ? `${number(row.current.latencySumMs / row.current.latencySamples)} ms`
                            : "Unknown"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section>
              <h3 className="mb-2 font-medium">Proposed actions</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Priority</th>
                      <th>Affected</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.actions.map((action, index) => (
                      <tr key={`${action.bindingKey}-${index}`} className="border-t border-border">
                        <td className="py-2">{index + 1}</td>
                        <td>{action.priority}</td>
                        <td>1 · {action.bindingKey}</td>
                        <td>{action.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!view.actions.length ? (
                <p className="text-sm text-muted-foreground">
                  No current failure/staleness action supported by this snapshot; not a fleet-health verdict.
                </p>
              ) : null}
            </section>
            {view.checkGroups.map((group) => (
              <figure key={group.key}>
                <figcaption className="mb-2 text-sm font-medium">
                  Daily recorded checks · {group.kind} · {group.environment}
                </figcaption>
                <div className="grid grid-cols-7 gap-1">
                  {group.daily.map((day) => (
                    <div
                      key={day.date}
                      className={`min-w-0 rounded border p-1 text-center ${day.recorded === 0 ? "border-dashed border-border text-muted-foreground" : day.failed ? "border-destructive text-destructive" : "border-border bg-muted"}`}
                    >
                      <span className="block text-[10px]">{day.date.slice(5)}</span>
                      <strong className="block font-semibold">{day.recorded ? day.failed : "—"}</strong>
                      <small className="block text-[10px]">{day.recorded ? `fail/${day.recorded}` : "no data"}</small>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Number = failed checks. Dash = no recorded checks. Prior {operations.prior.startInclusive} →{" "}
                  {operations.prior.endExclusive}.
                </p>
              </figure>
            ))}
            <section>
              <h3 className="mb-2 font-medium">Incidents · environment unknown</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                Overlapping includes incidents crossing the boundary. Unified and legacy are separate, not additive
                unique counts. {view.incidents.reasons.join(", ")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>System / window</th>
                      <th>Overlapping</th>
                      <th>Opened</th>
                      <th>Resolved</th>
                      <th>Ongoing at end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidentRows.map(([label, counts]) => (
                      <tr key={label} className="border-t border-border">
                        <td className="py-2">{label}</td>
                        <td>{number(counts?.overlapping ?? null)}</td>
                        <td>{number(counts?.opened ?? null)}</td>
                        <td>{number(counts?.resolved ?? null)}</td>
                        <td>{number(counts?.ongoingAtEnd ?? null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section>
              <h3 className="mb-2 font-medium">Deployment outcomes</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                Terminal events use completed time; running attempts use start time. Provider success is separate from
                evaluated health. {view.deployments.reasons.join(", ")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Project / environment / window</th>
                      <th>Succeeded / failed</th>
                      <th>Canceled / running</th>
                      <th>Evaluated / succeeded</th>
                      <th>Healthy / degraded / unhealthy</th>
                      <th>Analyzing / not evaluated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.deployments.rows.flatMap((row) =>
                      (["current", "prior"] as const).map((period) => (
                        <tr key={`${row.bindingKey}-${period}`} className="border-t border-border">
                          <td className="py-2">
                            {row.project} / {row.environment} / {period}
                          </td>
                          <td>
                            {row[period].succeeded} / {row[period].failed}
                          </td>
                          <td>
                            {row[period].canceled} / {row[period].running}
                          </td>
                          <td>
                            {row[period].evaluated} / {row[period].succeeded}
                          </td>
                          <td>
                            {row[period].healthy} / {row[period].degraded} / {row[period].healthFailed}
                          </td>
                          <td>
                            {row[period].analyzing} / {row[period].notEvaluated}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
              {!view.deployments.rows.length ? (
                <p className="text-sm text-muted-foreground">No mapped deployment history.</p>
              ) : null}
            </section>
          </div>
        </details>
      </CardContent>
    </Card>
  )
}
